// Validated blood pressure monitors patients can buy, with direct retailer product links.
// Monitors, prices, descriptions and urls are from "BP monitors - help to buy v1.0.xlsx" (MH2-2040).
//
// Every url must point at the retailer's product page itself, never a search or category page, so the
// patient can buy without navigating the retailer's site. Retailers with no url are hidden - Boots had
// no products listed in v1.0, so its buttons do not currently appear.
//
// Product photos live in public/images/monitors/.

export interface MonitorRetailer {
  name: string;
  // Direct product page. Leave undefined until confirmed - the button is hidden rather than guessed.
  url?: string;
}

export interface PurchaseMonitor {
  name: string;
  description: string;
  // Shown as given, so it can read as a range.
  price?: string;
  image?: string;
  retailers: MonitorRetailer[];
}

export const purchaseMonitors: PurchaseMonitor[] = [
  {
    name: "Omron M2 Essential",
    description:
      "Trusted brand with a simple one-button design, ideal for home blood pressure and pulse monitoring.",
    price: "£30",
    image: "/images/monitors/omron-m2-essential.jpg",
    retailers: [
      {
        name: "British Heart Foundation",
        url: "https://shop.bhf.org.uk/omron-m2-essential-blood-pressure-monitor-with-22-32cm-cuff",
      },
      { name: "Argos", url: "https://www.argos.co.uk/product/7710960" },
      { name: "Boots" },
      {
        name: "Amazon",
        url: "https://www.amazon.co.uk/Essential-AUTOMATIC-PRESSURE-MONITOR-HEM-7188/dp/B0GJF29LW4",
      },
      { name: "Tesco", url: "https://www.tesco.com/shop/en-GB/products/326314943" },
    ],
  },
  {
    name: "A&D Medical UA-611",
    description:
      "Our lowest-cost recommended monitor, offering accurate and reliable blood pressure and pulse measurement.",
    price: "£17 to £20",
    image: "/images/monitors/ua-611.jpg",
    retailers: [
      {
        name: "British Heart Foundation",
        url: "https://shop.bhf.org.uk/a-d-ua-611-blood-pressure-monitor",
      },
      { name: "Argos" },
      { name: "Boots" },
      {
        name: "Amazon",
        url: "https://www.amazon.co.uk/Medical-UA-611-Upper-Pressure-Monitor/dp/B00JQ7SZLO",
      },
      { name: "Tesco", url: "https://www.tesco.com/shop/en-GB/products/326193670" },
    ],
  },
  {
    name: "A&D Medical UA-651",
    description:
      "Adds extra features such as irregular heartbeat detection while remaining simple to use at home.",
    price: "£25",
    image: "/images/monitors/ua-651.jpg",
    retailers: [
      {
        name: "British Heart Foundation",
        url: "https://shop.bhf.org.uk/a-d-ua-651-blood-pressure-monitor",
      },
      { name: "Argos" },
      { name: "Boots" },
      {
        name: "Amazon",
        url: "https://www.amazon.co.uk/Medical-UA-651SL-Blood-Pressure-Monitors/dp/B00ZTTAX7W",
      },
      { name: "Tesco", url: "https://www.tesco.com/shop/en-GB/products/326193676" },
    ],
  },
];

// Buy buttons are branded so patients recognise where they are going. Colours are approximations of
// each retailer's public brand colour, and text is set per brand so it stays legible against it -
// Amazon's orange needs dark text. shortName keeps the buttons narrow enough to sit several to a row.
export const retailerBrands: Record<
  string,
  { shortName: string; background: string; text: string }
> = {
  "British Heart Foundation": { shortName: "BHF", background: "#e4003b", text: "#ffffff" },
  Argos: { shortName: "Argos", background: "#ed1c24", text: "#ffffff" },
  Boots: { shortName: "Boots", background: "#05054b", text: "#ffffff" },
  Amazon: { shortName: "Amazon", background: "#ff9900", text: "#212b32" },
  Tesco: { shortName: "Tesco", background: "#00539f", text: "#ffffff" },
};

// Not a purchase card - a category page for patients who want to see the full approved range.
export const bhfAllMonitorsUrl =
  "https://shop.bhf.org.uk/health-fitness/health-monitoring/blood-pressure-monitors";

export const purchaseDisclaimer =
  "You will purchase directly from the retailer. Red Star and NHS Greater Glasgow and Clyde do not " +
  "sell these products and do not receive commission. Prices and availability may change.";
